/**
 * Shared parser for the design-stream SSE responses (Vercel AI SDK v4 protocol).
 *
 * Consumed by both the chat first-generation flow (`useCreateConcept`) and the
 * remix flow (`remixApi`), so the streamed status / interim narrative / tokens
 * are handled by one canonical implementation.
 *
 * Protocol prefixes:
 * - 0: Text tokens (streamed response text)
 * - 2: Data payloads (status, interim_description, concept_created,
 *      generated_concept, generated_board, …)
 * - 3: Error
 * - 8: / d: annotation + finish markers (ignored)
 */

/** Palette entry with text description and LAB color */
interface PaletteEntry {
  text?: string;
  lab?: { l: number; a: number; b: number };
}

/** Concept palette with dominant, secondary, accent colors */
interface ConceptPalette {
  dominant?: PaletteEntry;
  secondary?: PaletteEntry;
  accent?: PaletteEntry;
}

/** Generated concept returned from the Express stream */
export interface GeneratedConcept {
  roomSceneUrl: string;
  title?: string;
  tag?: string;
  sellText?: string;
  space?: string;
  style?: string;
  location?: string;
  budget?: string;
  palette?: ConceptPalette;
}

/** Result from Express stream parsing */
export interface ExpressStreamResult {
  type:
    | "generated_concept"
    | "generated_board"
    | "misc_response"
    | "error"
    | "empty";
  generatedConcept?: GeneratedConcept;
  miscResponse?: string;
  errorMessage?: string;
}

/** Callbacks for incremental SSE event parsing. */
export interface SSEEventCallbacks {
  onToken?: (text: string) => void;
  onStatus?: (status: { stage: string; message: string }) => void;
  onPayload?: (payload: any) => void;
  onError?: (errorText: string) => void;
}

/**
 * Parse a Vercel AI SDK v4 protocol stream incrementally using ReadableStream.
 * Fires callbacks as each SSE line arrives instead of waiting for the full
 * response.
 */
export async function processExpressStreamIncremental(
  response: Response,
  callbacks: SSEEventCallbacks = {},
): Promise<ExpressStreamResult> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();

  let generatedConcept: GeneratedConcept | null = null;
  let sawGeneratedBoard = false;
  let streamedText = "";
  let errorText = "";
  let buffer = "";

  // Parse a single SSE line and fire the matching callback. Shared by the main
  // loop and the trailing-buffer flush so the protocol handling lives once.
  const handleLine = (rawLine: string): void => {
    const trimmed = rawLine.trim();
    if (!trimmed) return;

    // 0: Text tokens
    const textMatch = trimmed.match(/^0:(.+)$/);
    if (textMatch) {
      try {
        const tokenContent = JSON.parse(textMatch[1]);
        if (typeof tokenContent === "string") {
          streamedText += tokenContent;
          callbacks.onToken?.(tokenContent);
        }
      } catch {
        /* ignore parse errors */
      }
      return;
    }

    // 3: Errors
    const errorMatch = trimmed.match(/^3:(.+)$/);
    if (errorMatch) {
      try {
        errorText = JSON.parse(errorMatch[1]);
      } catch {
        errorText = errorMatch[1];
      }
      callbacks.onError?.(errorText);
      return;
    }

    // 2: Data payloads (status updates, generated_concept, etc.)
    const dataMatch = trimmed.match(/^2:(.+)$/);
    if (dataMatch) {
      try {
        const payload = JSON.parse(dataMatch[1]);
        const items = Array.isArray(payload) ? payload : [payload];
        for (const item of items) {
          if (item.type === "status") {
            callbacks.onStatus?.(item);
          } else {
            callbacks.onPayload?.(item);
            if (item.type === "generated_concept" && item.roomSceneUrl) {
              generatedConcept = item;
            } else if (
              item.type === "generated_board" &&
              typeof item.heroImageUrl === "string"
            ) {
              sawGeneratedBoard = true;
            }
          }
        }
      } catch {
        /* ignore parse errors */
      }
      return;
    }

    // d: and 8: are finish/annotation markers — ignore
  };

  let chunk = await reader.read();
  while (!chunk.done) {
    buffer += decoder.decode(chunk.value, { stream: true });

    // Process complete lines, keep the incomplete last line in the buffer.
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) handleLine(line);

    chunk = await reader.read();
  }

  // Flush any remaining content with no trailing newline.
  if (buffer.trim()) handleLine(buffer);

  if (errorText) {
    return { type: "error", errorMessage: errorText };
  }
  if (sawGeneratedBoard) {
    return { type: "generated_board" };
  }
  if (generatedConcept) {
    return { type: "generated_concept", generatedConcept };
  }
  if (streamedText.trim()) {
    return { type: "misc_response", miscResponse: streamedText.trim() };
  }
  return { type: "empty" };
}
